import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { catchErrorLogRet, mapList } from '../../common/rest.operators';
import { PeriodType } from '../../widget/date-range.widget.model';
import {
    OptionalColumn,
    OptionalColumnModelSetTransformer,
    validateOptionalColumns,
} from '../optional-column.insight.model';

import {
    CacheDateItem,
    CacheDateItemModelSetTransformer,
    validateCacheDate,
} from './cache-date.pd.model';
import {
    DataFilterOptions,
    DataFilterOptionsModelTransformer,
} from './data-filter-options.pd.model';
import {
    ExtMetricDataItem,
    MetricData,
    MetricModelSetTransformer,
    validateMetric,
} from './metric.pd.model';

@Injectable()
export class PerformanceDataService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    listCacheDates(
        periodType: PeriodType,
        userId?: number,
    ): Observable<CacheDateItem[]> {
        let params = new HttpParams().set('periodType', String(periodType));
        if (userId != null) {
            params = params.set('userId', String(userId));
        }
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/performance-data/dates`,
                { params },
            )
            .pipe(
                mapList(new CacheDateItemModelSetTransformer()),
                tap((model: CacheDateItem[]) => validateCacheDate(model)),
                catchError((error) => {
                    console.error(error);
                    throw new Error(
                        'performancedata/datelist/:periodTypeId failed',
                    );
                }),
            );
    }

    listOptionalColumns(): Observable<OptionalColumn[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/performance-data/optional-column-types`,
            )
            .pipe(
                mapList(new OptionalColumnModelSetTransformer()),
                tap((model) => validateOptionalColumns(model)),
                catchError((error) => {
                    console.error(error);
                    throw new Error(
                        'performancedata/optionalColumnTypes failed',
                    );
                }),
            );
    }

    listData(opts: DataFilterOptions): Observable<ExtMetricDataItem[]> {
        const params = new HttpParams()
            .set('viewType', String(opts.viewType))
            .set('periodType', String(opts.periodType));

        const transformed =
            new DataFilterOptionsModelTransformer().reverseTransform(opts);

        // TODO remove after old pi is removed
        if (!opts.outputCriteriaIdTypes && opts.outputCriteriaIdType != null) {
            opts.outputCriteriaIdTypes = [opts.outputCriteriaIdType];
        }

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/performance-data/data`,
                transformed,
                { params },
            )
            .pipe(mapList(), catchErrorLogRet([]));
    }

    listDataToMetricData(opts: DataFilterOptions): Observable<MetricData[]> {
        return this.listData(opts).pipe(
            map((model) => {
                return new MetricModelSetTransformer(
                    opts.displayNameCriteriaIdType,
                    opts.outputCriteriaIdTypes,
                ).transform(model);
            }),
            tap((model: MetricData[]) => validateMetric(model)),
            catchError((error) => {
                console.error(error);
                throw new Error(
                    'performancedata/data/:viewTypeId/:periodTypeId failed',
                );
            }),
        );
    }
}
